/**
 * useEEGTileCache
 *
 * Sprint S1+S2 — 200x playback performance
 *
 * IndexedDB-backed tile cache with background warm-up.
 *
 * On recording open:  warmCache() downloads ALL LOD-3 tiles in parallel batches
 * of 5 and stores them in IndexedDB.  The EEGViewer queryFn checks this cache
 * first, so subsequent tile requests are served at IndexedDB speed (~1 ms)
 * rather than network speed (~300–800 ms on cold parquet).
 *
 * Key design points
 * -  Singleton cache shared across all viewer instances (maximise hit rate).
 * -  EEGSegmentCache uses LRU eviction so 300 MB never exceeds budget.
 * -  Cache keys include backend speed bucket, transport mode, and Sprint 2
 *    view identity (montage / channel set / zoom):
 *    `${recordingId}_${startMs}_${endMs}_${level}_${speedBucket}_${transportMode}_${montage}_${channels}_${zoom}`.
 *    Both warmCache and the queryFn must round to the same ms boundaries and
 *    use the same playback-speed bucket + identity.
 * -  After a live (cache-miss) fetch, the raw buffer is copied and stored
 *    BEFORE the original is transferred to the decode worker.
 * -  prefetchWindowsBackground() is exported so the queryFn can speculatively
 *    fetch the next N windows into IndexedDB while the current one plays.
 */

import { useEffect, useRef, useCallback } from "react";
import {
  type CachedEEGSegmentEntry,
  EEGSegmentCache,
  type EEGTileTransportMode,
  type TileCacheIdentity,
} from "../services/EEGSegmentCache";
import { buildTileCacheKey } from "../utils/tileCacheIdentity";
import { alignTileWindowToBackend } from "../utils/tileWindowAlignment";
import { getEegPlaybackTiles } from "@features/EEG/services/eegPlaybackApi";
import logger from "@shared/utils/logger";

export type { TileCacheIdentity };

// ─── Singleton ────────────────────────────────────────────────────────────────

let _sharedCache: EEGSegmentCache | null = null;
type PrefetchControllerEntry = {
  recordingId: string;
  ownerKey: string;
  key: string;
  controller: AbortController;
  furthestPlannedStartSec: number | null;
  lastBaseNextStartSec: number | null;
  inFlightTileKeys: Set<string>;
  plannedWindowHistoryKeys: string[];
  drainRequested: boolean;
  drainReason: string | null;
};

type PrefetchAbortDetails = {
  reason: string;
  ownerId?: string;
  ownerKey?: string;
  previousKey?: string | null;
  nextKey?: string | null;
  drainOwnerKeys?: string[];
  isPlaying?: boolean;
  speed?: number;
  previousSpeed?: number | null;
  nextSpeed?: number | null;
  currentTimeSeconds?: number | null;
  dataWindowStartSeconds?: number | null;
};

type PrefetchAbortStatsEntry = {
  count: number;
  lastKey: string | null;
  lastAbortAt: number | null;
  lastOwnerKey: string | null;
  lastReason: string | null;
  lastAbortedWindows: string[];
};

const _prefetchControllers = new Map<
  string,
  PrefetchControllerEntry
>();
const _prefetchInFlight = new Map<string, Promise<void>>();
const _prefetchAbortStats = new Map<string, PrefetchAbortStatsEntry>();

export interface EEGTilePrefetchDebugSnapshot {
  controllerKey: string | null;
  controllerAborted: boolean;
  exactTileInFlight: boolean;
  recordingInFlightCount: number;
  matchingInFlightCount: number;
  matchingInFlightWindows: string[];
  abortCount: number;
  lastAbortAgeMs: number | null;
  /** Sprint 2: total in-flight prefetch requests for this recording. */
  queueDepth: number;
  staleTileDropCount: number;
}

type PrefetchPerfPayload = Record<
  string,
  string | number | boolean | null | undefined | string[]
>;

function parseTileWindowFromCacheKey(tileKey: string): string | null {
  const [, startMs, endMs] = tileKey.split("_");
  if (!startMs || !endMs) {
    return null;
  }
  return `${startMs}-${endMs}`;
}

function markPrefetchAbort(
  recordingId: string,
  entry: PrefetchControllerEntry,
  details: PrefetchAbortDetails,
): void {
  const current = _prefetchAbortStats.get(recordingId);
  const abortedTileWindows = Array.from(entry.inFlightTileKeys)
    .map(parseTileWindowFromCacheKey)
    .filter((value): value is string => Boolean(value));
  _prefetchAbortStats.set(recordingId, {
    count: (current?.count ?? 0) + 1,
    lastKey: entry.key,
    lastAbortAt: Date.now(),
    lastOwnerKey: details.ownerKey ?? entry.ownerKey,
    lastReason: details.reason,
    lastAbortedWindows: abortedTileWindows,
  });

  emitPrefetchPerfEvent("prefetchWindow", {
    phase: "controller-abort",
    recordingId,
    ownerId: details.ownerId ?? getPrefetchOwnerId(recordingId, entry.ownerKey),
    ownerKey: details.ownerKey ?? entry.ownerKey,
    controllerKey: entry.key,
    abortReason: details.reason,
    previousKey: details.previousKey ?? entry.key,
    nextKey: details.nextKey ?? null,
    ownerInFlightCount: entry.inFlightTileKeys.size,
    abortedTileWindowCount: abortedTileWindows.length,
    abortedTileWindows,
    isPlaying: details.isPlaying,
    speed: details.speed,
    previousSpeed: details.previousSpeed,
    nextSpeed: details.nextSpeed,
    currentTimeSeconds: details.currentTimeSeconds,
    dataWindowStartSeconds: details.dataWindowStartSeconds,
  });
}

function markPrefetchDrain(
  recordingId: string,
  entry: PrefetchControllerEntry,
  details: PrefetchAbortDetails,
): void {
  const drainedTileWindows = Array.from(entry.inFlightTileKeys)
    .map(parseTileWindowFromCacheKey)
    .filter((value): value is string => Boolean(value));
  emitPrefetchPerfEvent("prefetchWindow", {
    phase: "controller-drain-request",
    recordingId,
    ownerId: details.ownerId ?? getPrefetchOwnerId(recordingId, entry.ownerKey),
    ownerKey: details.ownerKey ?? entry.ownerKey,
    controllerKey: entry.key,
    drainReason: details.reason,
    previousKey: details.previousKey ?? entry.key,
    nextKey: details.nextKey ?? null,
    ownerInFlightCount: entry.inFlightTileKeys.size,
    drainedTileWindowCount: drainedTileWindows.length,
    drainedTileWindows,
    isPlaying: details.isPlaying,
    speed: details.speed,
    previousSpeed: details.previousSpeed,
    nextSpeed: details.nextSpeed,
    currentTimeSeconds: details.currentTimeSeconds,
    dataWindowStartSeconds: details.dataWindowStartSeconds,
  });
}

function shouldDrainPrefetchController(
  entry: PrefetchControllerEntry,
  details?: PrefetchAbortDetails,
): boolean {
  if (!details?.drainOwnerKeys?.length) {
    return false;
  }
  return details.drainOwnerKeys.includes(entry.ownerKey);
}

function emitPrefetchPerfEvent(
  kind: string,
  payload: PrefetchPerfPayload,
): void {
  if (typeof window === "undefined") return;
  const recorder = (window as any).__eegPlaybackPerfRecord;
  if (typeof recorder === "function") {
    recorder(kind, payload);
  }
}

function getSharedCache(): EEGSegmentCache {
  if (!_sharedCache) {
    _sharedCache = new EEGSegmentCache({
      maxSizeBytes: 150 * 1024 * 1024, // 150 MB — halved to reduce V8 heap pressure
      maxEntries: 1000,
      ttlMs: 4 * 60 * 60 * 1000, // 4-hour TTL per session
    });
  }
  return _sharedCache;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Tile window size used during background warm-up (ms). */
const WARM_TILE_MS = 20_000;

/** Concurrent requests during warm-up. Keep low to avoid starving playback. */
const WARM_BATCH_SIZE = 5;

/** Keep speculative playback fetches near-window-first instead of fanning out. */
const PLAYBACK_PREFETCH_BATCH_SIZE = 2;

/** Bound retained 200x play-plan history so uniqueness telemetry stays lightweight. */
const PREFETCH_WINDOW_HISTORY_LIMIT = 64;

function normalizeSpeedBucket(speed: number): number {
  const normalized = Math.trunc(Math.abs(Number(speed)));
  if (!Number.isFinite(normalized) || normalized < 1) {
    return 1;
  }
  return normalized;
}

function normalizeTransportMode(
  transportMode: EEGTileTransportMode | undefined,
): EEGTileTransportMode {
  return transportMode === "play" ? "play" : "inspect";
}

function getTileCacheKey(
  recordingId: string,
  startMs: number,
  endMs: number,
  level: number,
  speedBucket: number,
  transportMode: EEGTileTransportMode,
  identity?: TileCacheIdentity | null,
): string {
  return buildTileCacheKey({
    recordingId,
    startMs,
    endMs,
    level,
    speedBucket,
    transportMode,
    identity,
  });
}

/** Sprint 2: dropped stale tile responses (network arrived after context change). */
let _staleTileDropCount = 0;

export function recordStaleTileDrop(count = 1): void {
  _staleTileDropCount += Math.max(0, Math.floor(count));
}

export function getStaleTileDropCount(): number {
  return _staleTileDropCount;
}

export function resetStaleTileDropCount(): void {
  _staleTileDropCount = 0;
}

export function getTilePrefetchQueueDepth(recordingId?: string): number {
  if (!recordingId) {
    return _prefetchInFlight.size;
  }
  const prefix = `${recordingId}_`;
  let depth = 0;
  for (const key of _prefetchInFlight.keys()) {
    if (key.startsWith(prefix)) {
      depth += 1;
    }
  }
  return depth;
}

function getPrefetchOwnerId(recordingId: string, ownerKey: string): string {
  return `${recordingId}:${ownerKey}`;
}

function getRecordingPrefetchControllerEntries(
  recordingId: string,
): Array<[string, PrefetchControllerEntry]> {
  const recordingPrefix = `${recordingId}:`;
  return Array.from(_prefetchControllers.entries()).filter(
    ([ownerId, entry]) =>
      entry.recordingId === recordingId || ownerId.startsWith(recordingPrefix),
  );
}

function getPrefetchController(
  recordingId: string,
  ownerKey: string,
  prefetchKey: string,
): PrefetchControllerEntry {
  const ownerId = getPrefetchOwnerId(recordingId, ownerKey);
  const existing = _prefetchControllers.get(ownerId);
  if (existing && existing.key !== prefetchKey) {
    markPrefetchAbort(recordingId, existing, {
      reason: "controller-replaced",
      ownerId,
      ownerKey,
      previousKey: existing.key,
      nextKey: prefetchKey,
    });
    existing.controller.abort();
    _prefetchControllers.delete(ownerId);
  }

  const current = _prefetchControllers.get(ownerId);
  if (
    current &&
    current.key === prefetchKey &&
    !current.controller.signal.aborted
  ) {
    return current;
  }

  const entry = {
    recordingId,
    ownerKey,
    key: prefetchKey,
    controller: new AbortController(),
    furthestPlannedStartSec: null,
    lastBaseNextStartSec: null,
    inFlightTileKeys: new Set<string>(),
    plannedWindowHistoryKeys: [],
    drainRequested: false,
    drainReason: null,
  } satisfies PrefetchControllerEntry;
  _prefetchControllers.set(ownerId, entry);
  return entry;
}

export function cancelPrefetchRequests(
  recordingId?: string,
  details?: PrefetchAbortDetails,
): void {
  if (recordingId) {
    for (const [ownerId, current] of getRecordingPrefetchControllerEntries(
      recordingId,
    )) {
      if (shouldDrainPrefetchController(current, details)) {
        current.drainRequested = true;
        current.drainReason = details?.reason ?? "drain-recording";
        markPrefetchDrain(recordingId, current, {
          reason: details?.reason ?? "drain-recording",
          ownerId,
          ownerKey: current.ownerKey,
          previousKey: current.key,
          nextKey: null,
          isPlaying: details?.isPlaying,
          speed: details?.speed,
          previousSpeed: details?.previousSpeed,
          nextSpeed: details?.nextSpeed,
          currentTimeSeconds: details?.currentTimeSeconds,
          dataWindowStartSeconds: details?.dataWindowStartSeconds,
        });
        _prefetchControllers.delete(ownerId);
        continue;
      }
      markPrefetchAbort(recordingId, current, {
        reason: details?.reason ?? "cancel-recording",
        ownerId,
        ownerKey: current.ownerKey,
        previousKey: current.key,
        nextKey: null,
        isPlaying: details?.isPlaying,
        speed: details?.speed,
        previousSpeed: details?.previousSpeed,
        nextSpeed: details?.nextSpeed,
        currentTimeSeconds: details?.currentTimeSeconds,
        dataWindowStartSeconds: details?.dataWindowStartSeconds,
      });
      current.controller.abort();
      _prefetchControllers.delete(ownerId);
    }
    return;
  }

  for (const [ownerId, current] of _prefetchControllers.entries()) {
    if (shouldDrainPrefetchController(current, details)) {
      current.drainRequested = true;
      current.drainReason = details?.reason ?? "drain-all";
      markPrefetchDrain(current.recordingId, current, {
        reason: details?.reason ?? "drain-all",
        ownerId,
        ownerKey: current.ownerKey,
        previousKey: current.key,
        nextKey: null,
        isPlaying: details?.isPlaying,
        speed: details?.speed,
        previousSpeed: details?.previousSpeed,
        nextSpeed: details?.nextSpeed,
        currentTimeSeconds: details?.currentTimeSeconds,
        dataWindowStartSeconds: details?.dataWindowStartSeconds,
      });
      _prefetchControllers.delete(ownerId);
      continue;
    }
    markPrefetchAbort(current.recordingId, current, {
      reason: details?.reason ?? "cancel-all",
      ownerId,
      ownerKey: current.ownerKey,
      previousKey: current.key,
      nextKey: null,
      isPlaying: details?.isPlaying,
      speed: details?.speed,
      previousSpeed: details?.previousSpeed,
      nextSpeed: details?.nextSpeed,
      currentTimeSeconds: details?.currentTimeSeconds,
      dataWindowStartSeconds: details?.dataWindowStartSeconds,
    });
    current.controller.abort();
    _prefetchControllers.delete(ownerId);
  }
}

export function getPrefetchDebugSnapshot(
  recordingId: string,
  options: {
    level: number;
    speed: number;
    transportMode: EEGTileTransportMode;
    startMs?: number;
    endMs?: number;
    identity?: TileCacheIdentity | null;
  },
): EEGTilePrefetchDebugSnapshot {
  const normalizedMode = normalizeTransportMode(options.transportMode);
  const speedBucket = normalizeSpeedBucket(options.speed);
  const recordingControllerEntries = getRecordingPrefetchControllerEntries(
    recordingId,
  );
  const exactTileKey =
    typeof options.startMs === "number" && typeof options.endMs === "number"
      ? getTileCacheKey(
          recordingId,
          options.startMs,
          options.endMs,
          options.level,
          speedBucket,
          normalizedMode,
          options.identity,
        )
      : null;
  const recordingPrefix = `${recordingId}_`;
  const matchingMarker = `_${options.level}_${speedBucket}_${normalizedMode}_`;
  const recordingInFlightKeys = Array.from(_prefetchInFlight.keys()).filter(
    (key) => key.startsWith(recordingPrefix),
  );
  const matchingInFlightKeys = recordingInFlightKeys.filter((key) =>
    key.includes(matchingMarker),
  );
  const abortStats = _prefetchAbortStats.get(recordingId);

  return {
    controllerKey:
      recordingControllerEntries.length > 0
        ? recordingControllerEntries.map(([, entry]) => entry.key).join(",")
        : null,
    controllerAborted: recordingControllerEntries.some(
      ([, entry]) => entry.controller.signal.aborted,
    ),
    exactTileInFlight: exactTileKey ? _prefetchInFlight.has(exactTileKey) : false,
    recordingInFlightCount: recordingInFlightKeys.length,
    matchingInFlightCount: matchingInFlightKeys.length,
    matchingInFlightWindows: matchingInFlightKeys
      .slice(0, 4)
      .map((key) => key.split("_").slice(1, 3).join("-")),
    abortCount: abortStats?.count ?? 0,
    lastAbortAgeMs:
      typeof abortStats?.lastAbortAt === "number"
        ? Date.now() - abortStats.lastAbortAt
        : null,
    queueDepth: recordingInFlightKeys.length,
    staleTileDropCount: _staleTileDropCount,
  };
}

export async function waitForInFlightPrefetchTile(
  recordingId: string,
  startMs: number,
  endMs: number,
  level: number,
  speed: number,
  transportMode: EEGTileTransportMode = "inspect",
  identity?: TileCacheIdentity | null,
): Promise<boolean> {
  const tileKey = getTileCacheKey(
    recordingId,
    startMs,
    endMs,
    level,
    normalizeSpeedBucket(speed),
    normalizeTransportMode(transportMode),
    identity,
  );
  const inFlight = _prefetchInFlight.get(tileKey);
  if (!inFlight) {
    return false;
  }
  try {
    await inFlight;
  } catch {
    // Best effort only - caller can fall back to network fetch.
  }
  return true;
}

export interface EEGCachedTile {
  buffer: ArrayBuffer;
  tileRangeHeader: string | null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface EEGTileCacheHook {
  /**
   * Return a cached Arrow IPC buffer, or null on miss.
   * Key is exact — (startMs, endMs, level) must match what was stored.
   */
  getCached(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speed: number,
    transportMode?: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): Promise<ArrayBuffer | null>;

  /**
   * Return a cached Arrow IPC buffer together with the backend tile-range
   * header captured when the tile was originally fetched.
   */
  getCachedTile(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speed: number,
    transportMode?: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): Promise<EEGCachedTile | null>;

  /**
   * Store a tile buffer in IndexedDB.
   * Call BEFORE transferring the buffer to a Worker (ArrayBuffer is cloned).
   */
  putCached(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speed: number,
    buffer: ArrayBuffer,
    transportMode?: EEGTileTransportMode,
    tileRangeHeader?: string | null,
    identity?: TileCacheIdentity | null,
  ): Promise<void>;

  /**
   * Kick off a background download of all LOD tiles for a recording.
   * Safe to call multiple times — already-cached tiles are skipped.
   * Returns immediately; download runs in background.
   *
   * @param highSpeedBufferSec - If provided, also pre-fetches a single large
   *   tile of this duration (from 0) before the 20s chunk warmup.  This seeds
   *   the exact cache key that high-speed playback (e.g. 150x) will request
   *   (`bufferSize = min(3600, speed * timeScale * 2.5)`) so the first 150x
   *   play hits IndexedDB instead of a cold 8-second Parquet fetch.
   */
  warmCache(
    recordingId: string,
    durationMs: number,
    level: number,
    highSpeedBufferSec?: number,
    highSpeedSpeed?: number,
    warmInspectTiles?: boolean,
    highSpeedSeedWindowStartSeconds?: number[],
    highSpeedIdentity?: TileCacheIdentity | null,
  ): void;

  /**
   * Evict a specific tile from IndexedDB.
   * Use when a cached tile is found to be stale (e.g. hasSuspiciousDuration).
   */
  deleteCached(
    recordingId: string,
    startMs: number,
    endMs: number,
    level: number,
    speed: number,
    transportMode?: EEGTileTransportMode,
    identity?: TileCacheIdentity | null,
  ): Promise<void>;

  /**
   * Evict ALL cached tiles for a recording.
   * Use on first suspicious-tile detection to clear the entire stale tile set
   * in one pass — single-key eviction is insufficient because each treadmill
   * step uses a different IDB key.
   */
  clearRecording(recordingId: string): Promise<void>;

  /** Abort any in-progress warm. */
  cancelWarm(): void;

  /** Abort speculative playback prefetches for a recording. */
  cancelPrefetch(recordingId?: string, details?: PrefetchAbortDetails): void;

  /** Sprint 2: synchronous snapshot of IDB cache hit/miss stats. */
  getCacheStats(): Promise<{
    hitRate: number;
    hitCount: number;
    missCount: number;
    entryCount: number;
    totalSize: number;
    evictionCount: number;
  }>;
}

export function useEEGTileCache(): EEGTileCacheHook {
  const cancelRef = useRef<boolean>(false);

  const mapCachedTile = useCallback(
    (entry: CachedEEGSegmentEntry | null): EEGCachedTile | null => {
      if (!entry) {
        return null;
      }

      return {
        buffer: entry.data,
        tileRangeHeader: entry.metadata.tileRangeHeader ?? null,
      };
    },
    [],
  );

  useEffect(
    () => () => {
      cancelRef.current = true;
      cancelPrefetchRequests(undefined, { reason: "hook-unmount" });
    },
    [],
  );

  const getCached = useCallback(
    (
      recordingId: string,
      startMs: number,
      endMs: number,
      level: number,
      speed: number,
      transportMode: EEGTileTransportMode = "inspect",
      identity?: TileCacheIdentity | null,
    ): Promise<ArrayBuffer | null> =>
      getSharedCache().get(
        recordingId,
        startMs,
        endMs,
        level,
        normalizeSpeedBucket(speed),
        normalizeTransportMode(transportMode),
        identity,
      ),
    [],
  );

  const getCachedTile = useCallback(
    async (
      recordingId: string,
      startMs: number,
      endMs: number,
      level: number,
      speed: number,
      transportMode: EEGTileTransportMode = "inspect",
      identity?: TileCacheIdentity | null,
    ): Promise<EEGCachedTile | null> =>
      mapCachedTile(
        await getSharedCache().getEntry(
          recordingId,
          startMs,
          endMs,
          level,
          normalizeSpeedBucket(speed),
          normalizeTransportMode(transportMode),
          identity,
        ),
      ),
    [mapCachedTile],
  );

  const putCached = useCallback(
    async (
      recordingId: string,
      startMs: number,
      endMs: number,
      level: number,
      speed: number,
      buffer: ArrayBuffer,
      transportMode: EEGTileTransportMode = "inspect",
      tileRangeHeader: string | null = null,
      identity?: TileCacheIdentity | null,
    ): Promise<void> => {
      await getSharedCache().put({
        recordingId,
        startMs,
        endMs,
        level,
        speedBucket: normalizeSpeedBucket(speed),
        transportMode: normalizeTransportMode(transportMode),
        identity,
        data: buffer,
        metadata: {
          samplingRate: 0,
          numChannels: 0,
          channelNames: [],
          cached_at: Date.now(),
          size_bytes: buffer.byteLength,
          tileRangeHeader,
        },
      });
    },
    [],
  );

  const deleteCached = useCallback(
    (
      recordingId: string,
      startMs: number,
      endMs: number,
      level: number,
      speed: number,
      transportMode: EEGTileTransportMode = "inspect",
      identity?: TileCacheIdentity | null,
    ): Promise<void> => {
      const speedBucket = normalizeSpeedBucket(speed);
      const key = getTileCacheKey(
        recordingId,
        startMs,
        endMs,
        level,
        speedBucket,
        normalizeTransportMode(transportMode),
        identity,
      );
      return getSharedCache().delete(key);
    },
    [],
  );

  const getCacheStats = useCallback(
    () => getSharedCache().getStats(),
    [],
  );

  const clearRecording = useCallback(
    (recordingId: string): Promise<void> =>
      getSharedCache().clearRecording(recordingId),
    [],
  );

  const warmCache = useCallback(
    (
      recordingId: string,
      durationMs: number,
      level: number,
      highSpeedBufferSec?: number,
      highSpeedSpeed: number = 100,
      warmInspectTiles: boolean = true,
      highSpeedSeedWindowStartSeconds: number[] = [0],
      highSpeedIdentity?: TileCacheIdentity | null,
    ): void => {
      cancelRef.current = false;
      _warmCacheBackground(
        recordingId,
        durationMs,
        level,
        cancelRef,
        highSpeedBufferSec,
        highSpeedSpeed,
        warmInspectTiles,
        highSpeedSeedWindowStartSeconds,
        highSpeedIdentity,
      ).catch((err) =>
        logger.warn("[EEGTileCache] Background warm error", err),
      );
    },
    [],
  );

  const cancelWarm = useCallback((): void => {
    cancelRef.current = true;
  }, []);

  const cancelPrefetch = useCallback(
    (recordingId?: string, details?: PrefetchAbortDetails): void => {
      cancelPrefetchRequests(recordingId, details);
    },
    [],
  );

  return {
    getCached,
    getCachedTile,
    putCached,
    deleteCached,
    clearRecording,
    warmCache,
    cancelWarm,
    cancelPrefetch,
    getCacheStats,
  };
}

// ─── Background warm implementation ──────────────────────────────────────────

async function _warmCacheBackground(
  recordingId: string,
  durationMs: number,
  level: number,
  cancelRef: React.MutableRefObject<boolean>,
  highSpeedBufferSec?: number,
  highSpeedSpeed: number = 100,
  warmInspectTiles: boolean = true,
  highSpeedSeedWindowStartSeconds: number[] = [0],
  highSpeedIdentity?: TileCacheIdentity | null,
): Promise<void> {
  const cache = getSharedCache();
  const normalizedHighSpeed = normalizeSpeedBucket(highSpeedSpeed);

  if (highSpeedBufferSec && highSpeedBufferSec > 0 && !cancelRef.current) {
    const recordingDurationSec = Math.max(0, durationMs / 1000);
    const requestedWindowStarts =
      highSpeedSeedWindowStartSeconds.length > 0
        ? highSpeedSeedWindowStartSeconds
        : [0];
    const alignedSeedWindows = Array.from(
      new Map(
        requestedWindowStarts
          .map((windowStartSec) =>
            alignTileWindowToBackend({
              windowStartSec: Math.max(0, windowStartSec),
              bufferSizeSec: highSpeedBufferSec,
              speed: normalizedHighSpeed,
              transportMode: "play",
              recordingDurationSec,
            }),
          )
          .map((alignedWindow) => [
            `${alignedWindow.startMs}-${alignedWindow.endMs}`,
            alignedWindow,
          ]),
      ).values(),
    );

    await Promise.allSettled(
      alignedSeedWindows.map(async (alignedWindow) => {
        if (cancelRef.current) return;

        const existing = await cache.get(
          recordingId,
          alignedWindow.startMs,
          alignedWindow.endMs,
          level,
          normalizedHighSpeed,
          "play",
          highSpeedIdentity,
        );
        if (existing) return;

        logger.info(
          `[EEGTileCache] Pre-seeding high-speed tile ${alignedWindow.startSec.toFixed(0)}-${alignedWindow.endSec.toFixed(0)}s L${level} S${normalizedHighSpeed} for ${recordingId}`,
        );

        try {
          const response = await getEegPlaybackTiles(
            recordingId,
            {
              startMs: alignedWindow.startMs,
              endMs: alignedWindow.endMs,
              level,
              speed: normalizedHighSpeed,
              transportMode: "play",
              requestOwner: "warm-play-seed",
            },
            {
              timeout: 60_000,
              headers: { Accept: "application/vnd.apache.arrow.stream" },
              responseType: "arraybuffer",
            },
          );

          if (cancelRef.current) return;

          await cache.put({
            recordingId,
            startMs: alignedWindow.startMs,
            endMs: alignedWindow.endMs,
            level,
            speedBucket: normalizedHighSpeed,
            transportMode: "play",
            identity: highSpeedIdentity,
            data: response.data as ArrayBuffer,
            metadata: {
              samplingRate: 0,
              numChannels: 0,
              channelNames: [],
              cached_at: Date.now(),
              size_bytes: (response.data as ArrayBuffer).byteLength,
              tileRangeHeader:
                (response.headers?.["x-tile-range"] as string) ?? null,
            },
          });

          logger.info(
            `[EEGTileCache] High-speed tile pre-seeded ${alignedWindow.startSec.toFixed(0)}-${alignedWindow.endSec.toFixed(0)}s for ${recordingId}`,
          );
        } catch (err) {
          logger.warn(`[EEGTileCache] High-speed tile pre-seed failed`, err);
        }
      }),
    );
  }

  if (!warmInspectTiles) {
    logger.info(
      `[EEGTileCache] High-speed pre-seed complete; skipped inspect warm for ${recordingId}`,
    );
    return;
  }

  // Build list of 20-second tile windows
  const tiles: Array<[number, number]> = [];
  for (let startMs = 0; startMs < durationMs; startMs += WARM_TILE_MS) {
    tiles.push([startMs, Math.min(startMs + WARM_TILE_MS, durationMs)]);
  }

  logger.info(
    `[EEGTileCache] Warming ${tiles.length} LOD-${level} tiles for ${recordingId}`,
  );

  for (let i = 0; i < tiles.length; i += WARM_BATCH_SIZE) {
    if (cancelRef.current) {
      logger.debug("[EEGTileCache] Warm cancelled");
      return;
    }

    const batch = tiles.slice(i, i + WARM_BATCH_SIZE);

    await Promise.allSettled(
      batch.map(async ([startMs, endMs]) => {
        if (cancelRef.current) return;

        // Skip tiles already in cache
        const existing = await cache.get(
          recordingId,
          startMs,
          endMs,
          level,
          1,
          "inspect",
        );
        if (existing) return;

        try {
          const response = await getEegPlaybackTiles(
            recordingId,
            {
              startMs,
              endMs,
              level,
              speed: 1,
              transportMode: "inspect",
              requestOwner: "warm-inspect",
            },
            {
              timeout: 30_000,
              headers: { Accept: "application/vnd.apache.arrow.stream" },
              responseType: "arraybuffer",
            },
          );

          if (cancelRef.current) return;

          await cache.put({
            recordingId,
            startMs,
            endMs,
            level,
            speedBucket: 1,
            transportMode: "inspect",
            data: response.data as ArrayBuffer,
            metadata: {
              samplingRate: 0,
              numChannels: 0,
              channelNames: [],
              cached_at: Date.now(),
              size_bytes: (response.data as ArrayBuffer).byteLength,
              tileRangeHeader:
                (response.headers?.["x-tile-range"] as string) ?? null,
            },
          });
        } catch (err) {
          // Non-fatal — playback falls back to network fetch
          logger.warn(
            `[EEGTileCache] Warm tile failed ${startMs}-${endMs}ms L${level}`,
            err,
          );
        }
      }),
    );
  }

  logger.info(`[EEGTileCache] Warm complete for ${recordingId}`);
}

// ─── Exported prefetch helper (used inside EEGViewer queryFn) ────────────────

/**
 * Speculatively fetch the next `numWindows` tile windows into IndexedDB.
 * Fire-and-forget: call without awaiting from inside the queryFn.
 *
 * Window boundaries use the same alignment as EEGViewer's dataWindowStart so
 * cache keys match exactly on the next queryFn invocation.
 *
 * IMPORTANT: `speed` must match the viewer's playback speed so prefetched tiles
 * have the same resolution as what the viewer needs on cache hit.  Using a
 * different speed causes the backend to return downsampled data that is then
 * served back to the viewer, triggering the "severely truncated" fallback.
 */
export async function prefetchWindowsBackground(
  recordingId: string,
  currentWindowStartSec: number,
  advanceChunkSec: number,
  bufferSizeSec: number,
  level: number,
  numWindows: number,
  recordingDurationSec: number,
  speed: number = 16,
  transportMode: EEGTileTransportMode = "play",
  windowOffsetStart: number = 1,
  prefetchOwnerKey: string = "default",
  preserveDeeperProgress: boolean = false,
  protectedRunwayWindowCount: number = 0,
  identity?: TileCacheIdentity | null,
): Promise<void> {
  if (numWindows <= 0) {
    return;
  }

  const cache = getSharedCache();
  const normalizedMode = normalizeTransportMode(transportMode);
  const speedBucket = normalizeSpeedBucket(speed);
  const ownerId = getPrefetchOwnerId(recordingId, prefetchOwnerKey);
  const prefetchControllerEntry = getPrefetchController(
    recordingId,
    prefetchOwnerKey,
    `${recordingId}:${level}:${speedBucket}:${normalizedMode}:${buildTileCacheKey({
      recordingId,
      startMs: 0,
      endMs: 0,
      level,
      speedBucket,
      transportMode: normalizedMode,
      identity,
    })}`,
  );
  const prefetchController = prefetchControllerEntry.controller;

  const windows: Array<{
    nextStart: number;
    nextEnd: number;
    startMs: number;
    endMs: number;
    windowKey: string;
  }> = [];
  const queuedWindowKeys = new Set<string>();
  const normalizedWindowOffsetStart = Math.max(1, Math.trunc(windowOffsetStart));
  const baseNextStartSec =
    currentWindowStartSec + normalizedWindowOffsetStart * advanceChunkSec;
  const shouldPreserveDeeperProgress =
    preserveDeeperProgress && normalizedMode === "play" && speed >= 200;
  const normalizedProtectedRunwayWindowCount = shouldPreserveDeeperProgress
    ? Math.max(0, Math.trunc(protectedRunwayWindowCount))
    : 0;
  const effectiveWindowTargetCount = Math.max(
    numWindows,
    normalizedProtectedRunwayWindowCount,
  );
  const requestedFurthestStartSec =
    baseNextStartSec + Math.max(0, numWindows - 1) * advanceChunkSec;
  const targetFurthestStartSec =
    baseNextStartSec +
    Math.max(0, effectiveWindowTargetCount - 1) * advanceChunkSec;
  const shouldRecordBackgroundPrefetchTelemetry =
    normalizedMode === "play" &&
    speed >= 200 &&
    prefetchOwnerKey === "play-background";
  const maxOwnerConcurrentRequests =
    normalizedMode === "play" && speed >= 200 && prefetchOwnerKey === "play-background"
      ? 2
      : Number.POSITIVE_INFINITY;
  let nextStartCursor = baseNextStartSec;
  let runwayDecision = "fresh";
  const furthestPlannedBeforeSec =
    prefetchControllerEntry.furthestPlannedStartSec ?? null;
  const previousBaseNextStartSec =
    prefetchControllerEntry.lastBaseNextStartSec ?? null;

  if (shouldPreserveDeeperProgress) {
    const rewoundBeyondRunway =
      typeof previousBaseNextStartSec === "number" &&
      baseNextStartSec + advanceChunkSec < previousBaseNextStartSec;

    if (rewoundBeyondRunway) {
      prefetchControllerEntry.furthestPlannedStartSec = null;
      prefetchControllerEntry.plannedWindowHistoryKeys = [];
      runwayDecision = "reset";
    } else {
      const furthestPlannedStartSec = prefetchControllerEntry.furthestPlannedStartSec;
      if (typeof furthestPlannedStartSec === "number") {
        nextStartCursor = Math.max(baseNextStartSec, furthestPlannedStartSec + advanceChunkSec);
        runwayDecision =
          furthestPlannedStartSec >= targetFurthestStartSec
            ? "satisfied"
            : "carry-forward";
      }
    }
  }

  prefetchControllerEntry.lastBaseNextStartSec = baseNextStartSec;

  while (nextStartCursor <= targetFurthestStartSec) {
    const nextStart = nextStartCursor;
    if (nextStart >= recordingDurationSec) break;

    const alignedWindow = alignTileWindowToBackend({
      windowStartSec: nextStart,
      bufferSizeSec,
      speed,
      transportMode: normalizedMode,
      recordingDurationSec,
    });
    const nextEnd = alignedWindow.endSec;
    const startMs = alignedWindow.startMs;
    const endMs = alignedWindow.endMs;
    const windowKey = `${startMs}-${endMs}`;
    if (queuedWindowKeys.has(windowKey)) {
      continue;
    }
    queuedWindowKeys.add(windowKey);
    windows.push({ nextStart, nextEnd, startMs, endMs, windowKey });
    nextStartCursor += advanceChunkSec;
  }

  const repeatedPlannedWindows = shouldRecordBackgroundPrefetchTelemetry
    ? (() => {
        const historicalWindowKeys = new Set(
          prefetchControllerEntry.plannedWindowHistoryKeys,
        );
        const repeatedWindows = windows.filter((window) =>
          historicalWindowKeys.has(window.windowKey),
        );
        const newWindowKeys = windows
          .map((window) => window.windowKey)
          .filter((windowKey) => !historicalWindowKeys.has(windowKey));

        if (newWindowKeys.length > 0) {
          prefetchControllerEntry.plannedWindowHistoryKeys = [
            ...prefetchControllerEntry.plannedWindowHistoryKeys,
            ...newWindowKeys,
          ].slice(-PREFETCH_WINDOW_HISTORY_LIMIT);
        }

        return repeatedWindows;
      })()
    : [];

  if (shouldPreserveDeeperProgress && windows.length > 0) {
    prefetchControllerEntry.furthestPlannedStartSec =
      Math.max(
        prefetchControllerEntry.furthestPlannedStartSec ?? Number.NEGATIVE_INFINITY,
        windows[windows.length - 1]?.nextStart ?? Number.NEGATIVE_INFINITY,
      );
  }

  if (shouldRecordBackgroundPrefetchTelemetry) {
    emitPrefetchPerfEvent("prefetchWindow", {
      phase: "plan",
      ownerKey: prefetchOwnerKey,
      recordingId,
      speed,
      level,
      currentWindowStartSeconds: Number(currentWindowStartSec.toFixed(3)),
      advanceChunkSeconds: Number(advanceChunkSec.toFixed(3)),
      requestedWindowCount: numWindows,
      effectiveWindowTargetCount,
      protectedRunwayWindowCount: normalizedProtectedRunwayWindowCount,
      windowOffsetStart: normalizedWindowOffsetStart,
      baseNextStartSeconds: Number(baseNextStartSec.toFixed(3)),
      requestedFurthestStartSeconds: Number(requestedFurthestStartSec.toFixed(3)),
      targetFurthestStartSeconds: Number(targetFurthestStartSec.toFixed(3)),
      furthestPlannedBeforeSeconds:
        typeof furthestPlannedBeforeSec === "number"
          ? Number(furthestPlannedBeforeSec.toFixed(3))
          : null,
      previousBaseNextStartSeconds:
        typeof previousBaseNextStartSec === "number"
          ? Number(previousBaseNextStartSec.toFixed(3))
          : null,
      furthestPlannedAfterSeconds:
        typeof prefetchControllerEntry.furthestPlannedStartSec === "number"
          ? Number(prefetchControllerEntry.furthestPlannedStartSec.toFixed(3))
          : null,
      runwayDecision,
      plannedWindowCount: windows.length,
      newPlannedWindowCount: windows.length - repeatedPlannedWindows.length,
      repeatedPlannedWindowCount: repeatedPlannedWindows.length,
      repeatedPlannedWindowStartsSeconds:
        repeatedPlannedWindows.length > 0
          ? repeatedPlannedWindows
              .map((window) => window.nextStart.toFixed(3))
              .join(",")
          : "",
      plannedWindowUniquenessState:
        repeatedPlannedWindows.length > 0 ? "repeated" : "unique",
      plannedWindowStartsSeconds:
        windows.length > 0
          ? windows.map((window) => window.nextStart.toFixed(3)).join(",")
          : "",
    });
  }

  const prefetchWindow = async ({
    nextStart,
    nextEnd,
    startMs,
    endMs,
  }: {
    nextStart: number;
    nextEnd: number;
    startMs: number;
    endMs: number;
  }): Promise<void> => {
    if (prefetchController.signal.aborted) return;

    const emitWindowEvent = (
      phase: string,
      extra: PrefetchPerfPayload = {},
    ): void => {
      if (!shouldRecordBackgroundPrefetchTelemetry) return;
      emitPrefetchPerfEvent("prefetchWindow", {
        phase,
        ownerKey: prefetchOwnerKey,
        recordingId,
        speed,
        level,
        plannedStartSeconds: Number(nextStart.toFixed(3)),
        plannedEndSeconds: Number(nextEnd.toFixed(3)),
        startMs,
        endMs,
        ...extra,
      });
    };

    if (prefetchControllerEntry.drainRequested) {
      emitWindowEvent("drain-skip", {
        drainReason: prefetchControllerEntry.drainReason,
      });
      return;
    }

    const existing = await cache.get(
      recordingId,
      startMs,
      endMs,
      level,
      speedBucket,
      normalizedMode,
      identity,
    );
    if (existing) {
      emitWindowEvent("skip-cache");
      return;
    }

    const tileKey = getTileCacheKey(
      recordingId,
      startMs,
      endMs,
      level,
      speedBucket,
      normalizedMode,
      identity,
    );
    const inFlight = _prefetchInFlight.get(tileKey);
    if (inFlight) {
      emitWindowEvent("skip-in-flight", {
        controllerAborted: prefetchController.signal.aborted,
      });
      // At ultra-rapid play rates, later speculative passes need to keep
      // scanning for the first missing deeper tile instead of serializing
      // behind an already in-flight overlap window.
      if (normalizedMode === "play" && speed >= 200) {
        return;
      }
      await inFlight;
      return;
    }

    while (
      Number.isFinite(maxOwnerConcurrentRequests) &&
      prefetchControllerEntry.inFlightTileKeys.size >= maxOwnerConcurrentRequests
    ) {
      const ownerInFlight = Array.from(prefetchControllerEntry.inFlightTileKeys)
        .map((inFlightKey) => _prefetchInFlight.get(inFlightKey))
        .filter((value): value is Promise<void> => Boolean(value));

      if (!ownerInFlight.length) {
        break;
      }

      emitWindowEvent("wait-owner-cap", {
        ownerInFlightCount: prefetchControllerEntry.inFlightTileKeys.size,
        ownerConcurrencyLimit: maxOwnerConcurrentRequests,
      });

      await Promise.race(ownerInFlight).catch(() => {
        // Capacity checks are best-effort; individual request errors are handled below.
      });

      if (prefetchController.signal.aborted) {
        emitWindowEvent("abort");
        return;
      }

      if (prefetchControllerEntry.drainRequested) {
        emitWindowEvent("drain-skip", {
          drainReason: prefetchControllerEntry.drainReason,
        });
        return;
      }

      const cachedAfterWait = await cache.get(
        recordingId,
        startMs,
        endMs,
        level,
        speedBucket,
        normalizedMode,
        identity,
      );
      if (cachedAfterWait) {
        emitWindowEvent("skip-cache");
        return;
      }

      const inFlightAfterWait = _prefetchInFlight.get(tileKey);
      if (inFlightAfterWait) {
        emitWindowEvent("skip-in-flight", {
          controllerAborted: prefetchController.signal.aborted,
        });
        return;
      }
    }

    const request = (async () => {
      try {
        if (prefetchControllerEntry.drainRequested) {
          emitWindowEvent("drain-skip", {
            drainReason: prefetchControllerEntry.drainReason,
          });
          return;
        }
        emitWindowEvent("issue");
        const response = await getEegPlaybackTiles(
          recordingId,
          {
            startMs,
            endMs,
            level,
            speed,
            transportMode: normalizedMode,
            requestOwner: prefetchOwnerKey,
            channels: identity?.channels ? [...identity.channels] : undefined,
          },
          {
            timeout: 30_000,
            headers: { Accept: "application/vnd.apache.arrow.stream" },
            responseType: "arraybuffer",
            signal: prefetchController.signal,
          },
        );

        if (prefetchController.signal.aborted) return;

        await cache.put({
          recordingId,
          startMs,
          endMs,
          level,
          speedBucket,
          transportMode: normalizedMode,
          identity,
          data: response.data as ArrayBuffer,
          metadata: {
            samplingRate: 0,
            numChannels: 0,
            channelNames: [],
            cached_at: Date.now(),
            size_bytes: (response.data as ArrayBuffer).byteLength,
            tileRangeHeader:
              (response.headers?.["x-tile-range"] as string) ?? null,
          },
        });

        logger.debug(
          `[EEGTileCache] Prefetched window ${nextStart.toFixed(0)}s-${nextEnd.toFixed(0)}s L${level}`,
        );
        emitWindowEvent("complete", {
          responseSizeBytes: (response.data as ArrayBuffer).byteLength,
        });
      } catch (err) {
        if (prefetchController.signal.aborted) {
          emitWindowEvent("abort");
          return;
        }
        logger.warn(
          `[EEGTileCache] Prefetch failed ${nextStart.toFixed(0)}s-${nextEnd.toFixed(0)}s L${level}`,
          err,
        );
        emitWindowEvent("error", {
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      } finally {
        prefetchControllerEntry.inFlightTileKeys.delete(tileKey);
        _prefetchInFlight.delete(tileKey);
        if (
          prefetchControllerEntry.drainRequested &&
          prefetchControllerEntry.inFlightTileKeys.size === 0
        ) {
          emitPrefetchPerfEvent("prefetchWindow", {
            phase: "controller-drained",
            recordingId,
            ownerId,
            ownerKey: prefetchOwnerKey,
            controllerKey: prefetchControllerEntry.key,
            drainReason: prefetchControllerEntry.drainReason,
          });
        }
      }
    })();

    prefetchControllerEntry.inFlightTileKeys.add(tileKey);
    _prefetchInFlight.set(tileKey, request);
    await request;
  };

  const playbackPrefetchBatchSize =
    speed >= 200
      ? Math.min(
          2,
          Math.max(1, normalizedProtectedRunwayWindowCount || 1),
        )
      : PLAYBACK_PREFETCH_BATCH_SIZE;

  for (
    let index = 0;
    index < windows.length;
    index += playbackPrefetchBatchSize
  ) {
    if (prefetchController.signal.aborted || prefetchControllerEntry.drainRequested) {
      break;
    }
    await Promise.allSettled(
      windows
        .slice(index, index + playbackPrefetchBatchSize)
        .map(prefetchWindow),
    );
  }
}
